import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useContabilidades,
  useEnviarArquivosPorEmail,
  type FiltroArquivosFiscais,
} from '../../shared/api/queries.ts';
import type { Cliente, ResultadoEnvioPorEmail } from '../../shared/api/tipos.ts';
import { formatarDataSimples, formatarInteiro } from '../../shared/format.ts';

/**
 * Confirmação do envio do período por e-mail.
 *
 * O diálogo existe porque a ação é irreversível e sai do sistema: documento
 * fiscal indo para a caixa de outra pessoa. Antes de clicar, quem envia vê o
 * cliente, o período, quantas notas vão e para onde — um "Enviar" solto na
 * barra mandaria tudo isso no primeiro clique errado.
 *
 * A lista mostrada é a do cadastro, e é prévia: quem resolve os destinatários
 * é o servidor, no instante do envio. A confirmação que aparece depois traz a
 * lista de verdade, que é a única que prova para quem foi.
 *
 * O que vai na mensagem é um **link**, não um anexo: o pacote de uma
 * competência de PDV não cabe num e-mail, e o que caberia ficaria preso no
 * antivírus de quem recebe. O diálogo diz isso com todas as letras, porque
 * quem clica precisa saber o que o contador vai receber.
 *
 * A pendência do período aparece como aviso, e **não** impede o envio: há
 * motivo legítimo para entregar um mês com nota esperando a SEFAZ, e travar
 * aqui obrigaria o escritório a resolver com ela antes de poder trabalhar. O
 * que o aviso resolve é o outro caso — mandar sem saber, e descobrir depois
 * que o pacote saiu com menos nota do que a loja emitiu.
 */
export function DialogoEnvioPorEmail({
  cliente,
  filtro,
  quantidade,
  pendentes = 0,
  aoFechar,
  aoEnviado,
}: {
  /** O cliente do envio. `null` mantém o diálogo fechado. */
  cliente: Cliente | null;
  filtro: FiltroArquivosFiscais;
  /** Notas do período, para a pessoa saber o tamanho do que está mandando. */
  quantidade: number;
  /** Pendentes e rejeitadas do mesmo período, que não vão no pacote. */
  pendentes?: number;
  aoFechar: () => void;
  aoEnviado: (resultado: ResultadoEnvioPorEmail) => void;
}) {
  const contabilidades = useContabilidades();
  const enviar = useEnviarArquivosPorEmail();

  const contabilidade =
    cliente?.contabilidadeId === null || cliente === null
      ? undefined
      : contabilidades.data?.itens.find((item) => item.id === cliente.contabilidadeId);

  const doCliente = cliente?.emails ?? [];
  const daContabilidade = contabilidade?.emails ?? [];
  // A mesma união que o servidor faz, só para mostrar: um endereço repetido
  // entre cliente e contabilidade recebe uma cópia, não duas.
  const previa = [...new Set([...doCliente, ...daContabilidade])];

  const carregandoContabilidade =
    cliente !== null && cliente.contabilidadeId !== null && contabilidades.isPending;

  async function confirmar() {
    if (cliente === null) return;

    try {
      aoEnviado(await enviar.mutateAsync({ ...filtro, empresaId: cliente.id }));
      aoFechar();
    } catch {
      // A mensagem fica em `enviar.error`, dentro do diálogo: fechar aqui
      // esconderia justamente o motivo da recusa.
    }
  }

  return (
    <Dialog open={cliente !== null} onClose={aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Enviar arquivos por e-mail</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Cliente
            </Typography>
            <Typography variant="body1">{cliente?.razaoSocial}</Typography>
            <Typography variant="caption" color="text.secondary">
              {cliente?.cnpjFormatado}
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Período
            </Typography>
            <Typography variant="body1">
              {filtro.de === undefined || filtro.ate === undefined
                ? 'Todas as notas arquivadas'
                : `${formatarDataSimples(filtro.de)} a ${formatarDataSimples(filtro.ate)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {quantidade} nota(s) no filtro
            </Typography>
          </Stack>

          {/* Não trava o envio — avisa. O pacote leva só o que é documento
              fiscal, então a nota que ainda espera a SEFAZ fica de fora dele
              e da soma do período. */}
          {pendentes > 0 && (
            <Alert severity="warning">
              <AlertTitle sx={{ mb: 0.5 }}>
                Este cliente tem {formatarInteiro(pendentes)} nota(s) pendente(s) ou
                rejeitada(s) no período
              </AlertTitle>
              <Typography variant="body2">
                Elas não entram no pacote nem na soma: pendente espera resposta da SEFAZ e
                rejeitada voltou com erro, e nenhuma das duas é documento fiscal ainda. Dá
                para enviar assim mesmo — resolvê-las depois exige mandar o período de novo.
              </Typography>
            </Alert>
          )}

          <Alert severity="info" icon={<LinkOutlinedIcon fontSize="inherit" />}>
            <AlertTitle sx={{ mb: 0.5 }}>A mensagem leva um link, não um anexo</AlertTitle>
            <Typography variant="body2">
              O contador clica e baixa um .zip com os XMLs e a planilha de conferência
              juntos. O e-mail traz também um segundo link, para ele consultar outros
              períodos deste cliente sem precisar pedir ao escritório.
            </Typography>
          </Alert>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Destinatários
            </Typography>

            {carregandoContabilidade ? (
              <Skeleton variant="rounded" height={32} />
            ) : previa.length === 0 ? (
              <Alert severity="warning">
                Nenhum e-mail cadastrado para este cliente nem para a contabilidade dele.
                Cadastre o endereço em Clientes ou em Contabilidades.
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {previa.map((email) => (
                  <Chip
                    key={email}
                    size="small"
                    label={email}
                    variant={doCliente.includes(email) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            )}

            <Typography variant="caption" color="text.secondary">
              São os e-mails do cadastro do cliente (preenchidos) e os da contabilidade dele
              (contornados). Para mudar quem recebe, altere o cadastro.
            </Typography>
          </Stack>

          {enviar.isError && <Alert severity="error">{mensagemDoErro(enviar.error)}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={<SendOutlinedIcon />}
          onClick={() => void confirmar()}
          disabled={previa.length === 0 || quantidade === 0}
          loading={enviar.isPending}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
